import { useCallback, useRef } from 'react';
import { Howl } from 'howler';

const soundCache = new Map<string, Howl>();

function getSound(src: string): Howl {
  let sound = soundCache.get(src);
  if (!sound) {
    sound = new Howl({ src: [src], preload: true });
    soundCache.set(src, sound);
  }
  return sound;
}

// Preload effect sounds
['correct', 'wrong', 'success', 'click'].forEach(name => {
  getSound(`${process.env.PUBLIC_URL}/sounds/effects/${name}.mp3`);
});

const useSound = () => {
  const currentSoundId = useRef<number | null>(null);
  const currentHowl = useRef<Howl | null>(null);
  const muted = useRef(false);

  const stopCurrent = useCallback(() => {
    if (currentHowl.current && currentSoundId.current !== null) {
      currentHowl.current.stop(currentSoundId.current);
    }
    currentHowl.current = null;
    currentSoundId.current = null;
  }, []);

  const playFile = useCallback((src: string) => {
    if (muted.current) return;
    stopCurrent();
    const sound = getSound(src);
    const id = sound.play();
    currentHowl.current = sound;
    currentSoundId.current = id;
  }, [stopCurrent]);

  const speak = useCallback((text: string) => {
    if (muted.current) return;
    // Map text to an audio file path
    // Single Lao consonant → letter sound
    const letterFile = `${process.env.PUBLIC_URL}/sounds/letters/${text}.mp3`;
    // Number name → number sound (check by trying common names)
    const numberMap: Record<string, string> = {
      'ໜຶ່ງ': '1', 'ສອງ': '2', 'ສາມ': '3', 'ສີ່': '4', 'ຫ້າ': '5',
      'ຫົກ': '6', 'ເຈັດ': '7', 'ແປດ': '8', 'ເກົ້າ': '9', 'ສິບ': '10',
    };

    if (numberMap[text]) {
      playFile(`${process.env.PUBLIC_URL}/sounds/numbers/${numberMap[text]}.mp3`);
    } else if (text.length <= 3) {
      // Short text — likely a single character, try letter sound
      playFile(letterFile);
    } else {
      // Longer text (e.g. "ກ ໄກ່") — extract first char for the letter file
      const firstChar = text.charAt(0);
      playFile(`${process.env.PUBLIC_URL}/sounds/letters/${firstChar}.mp3`);
    }
  }, [playFile]);

  const playEffect = useCallback((type: 'correct' | 'wrong' | 'success' | 'click') => {
    if (muted.current) return;
    playFile(`${process.env.PUBLIC_URL}/sounds/effects/${type}.mp3`);
  }, [playFile]);

  const toggleMute = useCallback(() => {
    muted.current = !muted.current;
    if (muted.current) {
      stopCurrent();
    }
    return muted.current;
  }, [stopCurrent]);

  const isMuted = useCallback(() => muted.current, []);

  return { speak, playEffect, toggleMute, isMuted };
};

export default useSound;
